import { useCallback, useEffect, useRef, useState } from 'react';
import { getCheck, startCheck, unlockCheck } from './api';

const POLL_MS = 900;
const MAX_POLLS = 140; // about two minutes; the server caps a run at 75 s

// One site check, from "Check my site" to the finished report. With a token
// (the /check/:token page) it loads and, if still running, keeps polling.
export default function useSiteCheck({ token: initialToken = null } = {}) {
  const [phase, setPhase] = useState(initialToken ? 'running' : 'idle');
  const [check, setCheck] = useState(null);
  const [error, setError] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState('');
  const timer = useRef(null);
  const run = useRef(0); // bumps on every start/reset so late answers are ignored

  const stop = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };

  const poll = useCallback((token, id, count = 0) => {
    getCheck(token).then((next) => {
      if (id !== run.current) return;
      setCheck(next);
      if (next.status === 'done') { setPhase('done'); return; }
      if (next.status === 'failed') { setPhase('failed'); setError(next.error || 'The check could not finish.'); return; }
      if (count >= MAX_POLLS) { setPhase('failed'); setError('This is taking too long. Try again in a minute.'); return; }
      timer.current = setTimeout(() => poll(token, id, count + 1), POLL_MS);
    }).catch((err) => {
      if (id !== run.current) return;
      setPhase('failed');
      setError(err.message || 'The check could not finish.');
    });
  }, []);

  useEffect(() => {
    if (!initialToken) return undefined;
    const id = ++run.current;
    poll(initialToken, id);
    return () => { run.current += 1; stop(); };
  }, [initialToken, poll]);

  useEffect(() => () => { run.current += 1; stop(); }, []);

  const start = useCallback(async ({ url }) => {
    stop();
    const id = ++run.current;
    setPhase('starting'); setError(''); setCheck(null); setUnlockError('');
    try {
      const { token } = await startCheck({ url });
      if (id !== run.current) return;
      setPhase('running');
      poll(token, id);
    } catch (err) {
      if (id !== run.current) return;
      setPhase('failed');
      setError(err.message || 'The check could not start.');
    }
  }, [poll]);

  const unlock = useCallback(async ({ email, name }) => {
    if (!check) return;
    setUnlocking(true); setUnlockError('');
    try { setCheck(await unlockCheck(check.token, { email, name })); }
    catch (err) { setUnlockError(err.message || 'That did not work. Try again.'); }
    finally { setUnlocking(false); }
  }, [check]);

  const reset = useCallback(() => { stop(); run.current += 1; setPhase('idle'); setCheck(null); setError(''); }, []);

  return { phase, check, error, start, unlock, unlocking, unlockError, reset };
}
